import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useGoogleAnalyticsEventContext, GoogleAnalyticsConstants } from '@vvoyages/google-analytics';
import classNames from 'classnames';

import styles from './ToolTip.module.scss';
import Card from '../Card';

type ToolTipType = {
  parent: React.ReactNode;
  children: React.ReactNode;
  widthInRem: string;
  classWrapper: string;
  classTriangle: string;
  trackLabel: string;
  tabIndex?: number;
};

const ToolTip = ({
  parent,
  children,
  widthInRem,
  classWrapper = '',
  classTriangle = '',
  trackLabel,
  tabIndex = 0,
}: ToolTipType) => {
  const tooltip = useRef<HTMLDivElement>(null);
  const googleAnalyticsEventContext = useGoogleAnalyticsEventContext();
  const [isTooltipActive, setIsTooltipActive] = useState<boolean>(false);

  useEffect(() => {
    const doc = document.querySelector(':root') as HTMLElement;
    doc.style.setProperty('--ddwidth', widthInRem);
    // eslint-disable-next-line no-unsafe-optional-chaining
    doc.style.setProperty('--ddtranslate', `-${parseFloat(widthInRem) / 2}rem`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activateTooltip = useCallback(() => {
    setIsTooltipActive(true);
  }, []);

  const deactivateTooltip = useCallback(() => {
    setIsTooltipActive(false);
  }, []);

  useEffect(() => {
    const trackFunc = () => {
      googleAnalyticsEventContext.event(GoogleAnalyticsConstants.EVENT_ACTION.ICON_CLICK, trackLabel);
    };

    const tooltipElement = tooltip.current;

    if (tooltipElement) {
      tooltipElement.addEventListener('mouseenter', trackFunc);
      tooltipElement.addEventListener('touchstart', trackFunc);
      tooltipElement.addEventListener('focus', activateTooltip);
      tooltipElement.addEventListener('blur', deactivateTooltip);
    }

    return () => {
      if (tooltipElement) {
        tooltipElement.removeEventListener('mouseenter', trackFunc);
        tooltipElement.removeEventListener('touchstart', trackFunc);
        tooltipElement.removeEventListener('focus', activateTooltip);
        tooltipElement.removeEventListener('blur', deactivateTooltip);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={tooltip}
      className={classNames(styles.tooltip, { [styles.tooltip__active]: isTooltipActive })}
      tabIndex={tabIndex}
    >
      <div className={styles.parent}>{parent}</div>
      <Card className={classNames(styles.tooltip__content, classWrapper)}>
        <div>{children}</div>
        <div className={classNames(styles.tooltip__triangle, classTriangle)} />
      </Card>
    </div>
  );
};

export default ToolTip;
